'use client'
import { PopupWrapper } from '@/components/common'
import { RHFTextField } from '@/components/form'
import { Form } from '@/components/form/form-provider'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Grid, Rating, Typography, Stack, Box } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useRef, useState, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { EditorReview } from '@/features/reviews'
import { createComprehensiveReview } from '@/services/products'
import type { ICreateReviewInput } from '@/features/products/types/review'
import { useAccountStore } from '@/store'
import {
  reviewSchemaGuest,
  reviewSchemaAuthenticated,
  IReviewFormValuesAuthenticated,
  IReviewFormValuesGuest,
} from '@/validations'

interface IPanelPopupFormReviewProps {
  productId: number
  onSuccess?: () => void
}

export default function PanelPopupFormReview({ productId, onSuccess }: IPanelPopupFormReviewProps) {
  const [ratingHover, setRatingHover] = useState<number>(-1)
  const [comment, setComment] = useState<string>('')
  const editorRef = useRef<any>(null)
  const [open, setOpen] = useState(false)
  const [openSuccessPopup, setOpenSuccessPopup] = useState(false)
  const t = useTranslations('UserProfile')

  // Lấy thông tin user từ store
  const { isAuthenticated } = useAccountStore()

  // Sử dụng schema và type phù hợp dựa trên trạng thái đăng nhập
  const method = useForm<IReviewFormValuesAuthenticated | IReviewFormValuesGuest>({
    resolver: yupResolver((isAuthenticated ? reviewSchemaAuthenticated : reviewSchemaGuest) as any),
    defaultValues: isAuthenticated
      ? {
          rating: 5,
          content: '',
        }
      : {
          rating: 5,
          content: '',
          name: '',
        },
    mode: 'onChange',
  })
  const {
    handleSubmit: rhfHandleSubmit,
    control,
    formState: { errors, isSubmitting },
    watch,
    reset,
    setValue,
    clearErrors,
  } = method

  const currentRating = watch('rating')

  // Sync comment với form content
  useEffect(() => {
    setValue('content', comment, { shouldValidate: true })
  }, [comment, setValue])

  // Reset form khi mở dialog hoặc thay đổi trạng thái đăng nhập
  useEffect(() => {
    if (open) {
      reset(
        isAuthenticated
          ? {
              rating: 5,
              content: '',
            }
          : {
              rating: 5,
              content: '',
              name: '',
            }
      )
      setComment('')
      setRatingHover(-1)
    }
  }, [open, reset, isAuthenticated])

  const handleSubmit = async (data: IReviewFormValuesAuthenticated | IReviewFormValuesGuest) => {
    // Validate content từ editor
    if (!productId) {
      toast.error('Không tìm thấy thông tin sản phẩm')
      return
    }

    try {
      // Lấy URLs của media đã upload (images, videos)
      const mediaData = await editorRef.current?.getMediaUrls()

      // Format media theo đúng cấu trúc backend yêu cầu
      let formattedMedia: any[] = []

      if (mediaData && !Array.isArray(mediaData)) {
        // Nếu trả về object { url: string[], type: string }
        const urls = Array.isArray(mediaData.url) ? mediaData.url : [mediaData.url]
        formattedMedia = urls.map((url: string) => ({
          url: url, // Đường dẫn relative từ server
          type: mediaData.type || 'image', // 'image' hoặc 'video'
          thumbnail: url, // Thumbnail (bắt buộc)
        }))
      } else if (Array.isArray(mediaData)) {
        // Nếu trả về mảng string
        formattedMedia = mediaData.map((url: string) => {
          // Phát hiện type từ extension
          const ext = url.split('.').pop()?.toLowerCase()
          const isVideo = ['mp4', 'webm', 'ogg'].includes(ext || '')

          return {
            url: url,
            type: isVideo ? 'video' : 'image',
            thumbnail: url,
          }
        })
      }

      // Chuẩn bị data để gửi API - match với ICreateReviewInput
      const reviewInput: ICreateReviewInput = {
        product_id: productId,
        rating: data.rating,
        ...(!isAuthenticated && 'name' in data && { author_name: data.name }),
        content: data.content,
        ...(formattedMedia && formattedMedia.length > 0 && { media: formattedMedia }),
      }

      // Gọi API tạo review
      const response = await createComprehensiveReview(reviewInput)

      if (response.status === 'success') {
        // Reset form và close popup
        reset()
        setComment('')
        editorRef.current?.reset()
        setOpen(false)

        // Hiển thị popup thành công
        setOpenSuccessPopup(true)

        // Callback sau khi thành công
        onSuccess?.()
      } else {
        toast.error(response.message || 'Có lỗi xảy ra khi gửi đánh giá')
      }
    } catch (error) {
      toast.error('Có lỗi xảy ra khi gửi đánh giá')
      console.error('Submit review error:', error)
    } finally {
    }
  }
  return (
    <>
      <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
        Viết đánh giá
      </Button>

      {/* Popup form đánh giá */}
      <PopupWrapper
        isOpen={open}
        width="1000px"
        handleClose={() => setOpen(false)}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 2,
            padding: { xs: 0, md: 2 },
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          },
        }}
      >
        <Stack mb={3} spacing={1} alignItems="center">
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.25rem', md: '1.5rem' },
              letterSpacing: '0.5px',
            }}
          >
            ĐÁNH GIÁ SẢN PHẨM
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: '0.75rem', md: '0.925rem' },
              color: 'text.secondary',
              maxWidth: 300,
              textAlign: 'center',
            }}
          >
            Chia sẻ cảm nhận của bạn để giúp những khách hàng khác có lựa chọn tốt hơn
          </Typography>
        </Stack>
        <Form methods={method} onSubmit={rhfHandleSubmit(handleSubmit)}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Stack alignItems="center">
                <Controller
                  name="rating"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Stack alignItems="center" spacing={0.5}>
                      <Rating
                        {...field}
                        onChange={(_, newValue) => {
                          field.onChange(newValue || 0)
                        }}
                        onChangeActive={(_, newHover) => {
                          setRatingHover(newHover || 0)
                        }}
                        size="large"
                        sx={{
                          fontSize: {
                            xs: '1.5rem',
                            md: '2rem',
                          },
                          '& .MuiRating-iconFilled': {
                            color: 'rgba(255, 180, 0, 1)',
                          },
                        }}
                      />
                      {error && (
                        <Typography color="error" variant="caption">
                          {error.message}
                        </Typography>
                      )}
                    </Stack>
                  )}
                />
              </Stack>
            </Grid>
            {!isAuthenticated && (
              <Grid size={12}>
                <RHFTextField
                  name="name"
                  required
                  label="Họ và tên"
                  placeholder="Nhập họ và tên của bạn"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
            )}
            <Grid size={12}>
              <Typography
                sx={{
                  mb: 1,
                  fontWeight: 300,
                }}
              >
                Nội dung đánh giá <span style={{ color: '#d32f2f' }}>*</span>
              </Typography>
              <EditorReview
                ref={editorRef}
                value={comment}
                onChange={setComment}
                placeholder="Hãy chia sẻ trải nghiệm của bạn về sản phẩm này..."
                disabled={isSubmitting}
                rows={4}
                maxImages={5}
                maxVideos={1}
              />
              {errors.content && (
                <Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                  {errors.content.message}
                </Typography>
              )}
            </Grid>
            <Grid size={12}>
              <Button
                type="submit"
                variant="contained"
                disabled={currentRating === 0 || isSubmitting}
                fullWidth
                sx={{
                  borderRadius: '999px',
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.5,
                  maxWidth: 300,
                  margin: '0 auto',
                  display: 'block',
                }}
              >
                {isSubmitting ? 'Đang gửi...' : 'Gửi đánh giá'}
              </Button>
            </Grid>
          </Grid>
        </Form>
      </PopupWrapper>

      {/* Popup thông báo thành công */}
      <PopupWrapper
        isOpen={openSuccessPopup}
        width="500px"
        handleClose={() => setOpenSuccessPopup(false)}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 3,
            padding: { xs: 1, md: 2 },
          },
        }}
      >
        <Stack spacing={3} alignItems="center" textAlign="center">
          {/* Icon thành công */}
          <Box
            sx={{
              width: { xs: 60, md: 80 },
              height: { xs: 60, md: 80 },
              borderRadius: '50%',
              backgroundColor: '#d4edda',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'scaleIn 0.3s ease-out',
              '@keyframes scaleIn': {
                from: {
                  transform: 'scale(0)',
                },
                to: {
                  transform: 'scale(1)',
                },
              },
            }}
          >
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
              <path
                d="M20 6L9 17L4 12"
                stroke="#28a745"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Box>

          {/* Tiêu đề */}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.25rem', md: '1.5rem' },
              color: '#28a745',
            }}
          >
            Gửi đánh giá thành công!
          </Typography>

          {/* Nội dung */}
          <Typography
            sx={{
              fontSize: { xs: '0.875rem', md: '1rem' },
              color: 'text.secondary',
              lineHeight: 1.6,
            }}
          >
            Cảm ơn bạn đã chia sẻ đánh giá! Đánh giá của bạn đang được chờ quản trị viên phê duyệt và sẽ sớm
            được hiển thị.
          </Typography>

          {/* Button đóng */}
          <Button
            variant="contained"
            onClick={() => setOpenSuccessPopup(false)}
            sx={{
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 600,
              py: 1.5,
              px: 4,
              mt: 2,
            }}
          >
            Đã hiểu
          </Button>
        </Stack>
      </PopupWrapper>
    </>
  )
}
